import axios from 'axios';
import { BaseService } from './BaseService';

export class UserService extends BaseService {

    async getUsers(page = 0, first = 0, rows = 10, sortField = 'id', sortOrder = 1, filters, isAdmin = false, search = '', preset = null, subscriptionStatus = null, medicalServiceId = null) {
        filters = {
            ...filters,
            isHidden: { operator: 'and', constraints: [{matchMode: 'equals', value: false }] },
            isAdmin: { operator: 'and', constraints: [{matchMode: 'equals', value: isAdmin }] }
        }

        let newFilter = this.getFilters(filters);
        let order = sortOrder == 1 ? 'asc' : 'desc';
        if (sortField == 'name') {
            sortField = [`firstName ${order}`, `lastName ${order}`]
        } else {
            sortField = `${sortField} ${sortOrder == 1 ? 'asc' : 'desc'}`;
        }

        let parameters = {
            "offset": first,
            "limit": rows,
            "skip": first,
            "order": sortField,
            "include": [
                {
                    "relation": "residences",
                    "scope": {
                        "order": "id asc"
                    }
                },
                {
                    "relation": "medicalServices",
                    "scope": {
                        "order": "id asc"
                    }
                }
            ]
        };

        const where = this.buildWhere(newFilter, search);
        if (where) {
            parameters["where"] = where;
        }

        const presetParam = preset ? `&preset=${encodeURIComponent(preset)}` : '';
        const subscriptionStatusParam = subscriptionStatus ? `&subscriptionStatus=${encodeURIComponent(subscriptionStatus)}` : '';
        const medicalServiceIdParam = medicalServiceId ? `&medicalServiceId=${encodeURIComponent(medicalServiceId)}` : '';
        let res = await this.get(`/users?filter=${encodeURI(JSON.stringify(parameters))}${presetParam}${subscriptionStatusParam}${medicalServiceIdParam}`, true).then(res => res.data);
        return {
            data: res,
            page: page
        }
    }

    async getCountUsers(filters, isAdmin = false, search = '', preset = null, subscriptionStatus = null, medicalServiceId = null) {

        filters = {
            ...filters,
            isAdmin: { operator: 'and', constraints: [{matchMode: 'equals', value: isAdmin }] },
            isHidden: { operator: 'and', constraints: [{matchMode: 'equals', value: false }] }
        }

        let newFilter = this.getFilters(filters);
        const where = this.buildWhere(newFilter, search);
        const params = [];

        if (where) {
            params.push(`where=${encodeURI(JSON.stringify(where))}`);
        }
        if (preset) {
            params.push(`preset=${encodeURIComponent(preset)}`);
        }
        if (subscriptionStatus) {
            params.push(`subscriptionStatus=${encodeURIComponent(subscriptionStatus)}`);
        }
        if (medicalServiceId) {
            params.push(`medicalServiceId=${encodeURIComponent(medicalServiceId)}`);
        }
        const strWhere = params.length > 0 ? `?${params.join('&')}` : '';
        return this.get(`/users/count${strWhere}`, true).then(res => res.count);
    }

    // Combina os filtros de coluna (isAdmin/isHidden/etc, já no formato
    // LoopBack) com a busca livre por nome/email/celular, igual à busca
    // livre já usada no ranking de alunos.
    // firstName/lastName são colunas separadas, então buscar "Nome
    // Sobrenome" inteiro não bate com nenhuma delas isolada. O ranking
    // resolve isso com CONCAT no SQL bruto — aqui é via LoopBack ORM (sem
    // SQL bruto), então quebra a busca em palavras e exige que CADA palavra
    // bata em algum dos 4 campos (AND de ORs): "João Silva" vira
    // (algum campo contém "João") AND (algum campo contém "Silva").
    buildWhere(columnFilters, search) {
        const clauses = [];
        if (columnFilters) clauses.push(columnFilters);
        if (search && search.trim()) {
            const words = search.trim().split(/\s+/).filter(Boolean);
            const wordClauses = words.map(word => {
                const term = `%${word}%`;
                return {
                    or: [
                        { firstName: { like: term } },
                        { lastName: { like: term } },
                        { email: { like: term } },
                        { phone: { like: term } }
                    ]
                };
            });
            clauses.push(wordClauses.length === 1 ? wordClauses[0] : { and: wordClauses });
        }
        if (clauses.length === 0) return null;
        if (clauses.length === 1) return clauses[0];
        return { and: clauses };
    }

    async getUsersMock() {
        return axios.get('assets/features/data/users.json').then(res => res.data);
    }

    async getCountUsersMock() {
        return axios.get('assets/features/data/usersCount.json').then(res => res.data.count);
    }

    async sendWelcomeEmail(email) {
        const data = { email };
        return this.post(`/users/request-registration-code`, data, true);
    }

    async sendWelcomeEmailMock(email) {
        console.log('>> sendWelcomeEmail()', email)
        return axios.get('assets/features/data/welcomeEmailResponse.json').then(res => res.data);
    }

    async sendPasswordEmail(email) {
        const data = { email };
        return this.post(`/users/password-recovery`, data, true);
    }

    async sendPasswordEmailMock(email) {
        console.log('>> sendPasswordEmail()', email)
        return axios.get('assets/features/data/passwordEmailResponse.json').then(res => res.data);
    }

    async getUser(id) {
        return this.get(`/users/${id}`, true);
    }

    // Endpoint dedicado (já anexa createdAt da tabela de junção
    // UserMedicalService) — usado no accordion "Serviços" da tela de detalhes.
    async getMedicalServices(userId) {
        return this.get(`/users/${userId}/medical-services`, true);
    }

    // Idem, pra "Residências" (createdAt vem da tabela de junção UserResidence).
    async getResidences(userId) {
        return this.get(`/users/${userId}/residences`, true);
    }

    // Histórico completo do usuário (não paginado no servidor — é sempre
    // pequeno, poucas linhas por aluno), pra aba "Assinaturas" da tela de
    // detalhes. Pagina no navegador mesmo.
    async getSubscriptions(email) {
        const filter = { where: { user_email: email }, order: ['start_on desc'] };
        return this.get(`/subscriptions?filter=${encodeURI(JSON.stringify(filter))}`, true);
    }

    // Mesma ideia pra aba "Dispositivos".
    async getDevices(userId) {
        const filter = { order: ['createdAt desc'] };
        return this.get(`/users/${userId}/devices?filter=${encodeURI(JSON.stringify(filter))}`, true);
    }

    // Linha do tempo de sessões (AppSession), paginada no servidor —
    // consumida pela página de Histórico de Atividades.
    async getActivityTimeline(userId, { offset, limit } = {}) {
        const params = this.buildQueryString({ offset, limit });
        return this.get(`/sessions/analytics/user/${userId}/timeline${params}`, true);
    }

    // Uso diário (duração total + contagem de sessões por dia), consumido
    // pelo heatmap de calendário no topo da página de Histórico de
    // Atividades.
    async getDailyUsage(userId, { from, to } = {}) {
        const params = this.buildQueryString({ from, to });
        return this.get(`/sessions/analytics/user/${userId}/daily${params}`, true);
    }

    // Desativa todos os dispositivos do usuário (sem "where" = afeta todos os
    // dele) — não bloqueia acesso, só limpa o "dispositivo ativo" atual; o
    // próximo app aberto (dele ou de outro dispositivo) se registra sozinho
    // como o novo ativo (mesmo comportamento de sempre, só forçado agora).
    async resetDevices(userId) {
        return this.patch(`/users/${userId}/devices`, { active: false }, true);
    }

    async bulkSetSubscriptionStatus(userIds, active, reason) {
        return this.patch(`/subscriptions/bulk-status`, { userIds, active, reason }, true);
    }

    getFilters(filters = {}) {
        let newFilter = null;

        const fromTo = {
            'contains': 'like',
            'equals': 'eq',
            'startsWith': 'inq'
        };

        if (filters) {
            for (let [key, value] of Object.entries(filters)) {
                console.log(`${key}:`, value);
                if (value.constraints[0].value != null) {
                    let newValue = {};

                    let operator = fromTo[value.constraints[0].matchMode];

                    if (operator == 'like') {
                        newValue[operator] = `%${value.constraints[0].value}%`;
                    } else {
                        newValue[operator] = value.constraints[0].value;
                    }

                    if (newFilter == null) {
                        newFilter = {}
                    }

                    // if (key == 'name') {
                    //     newFilter['or'] = [{ 'firstName': newValue.split(' ') }, { 'lastName': newValue.split(' ') }];
                    //     // newFilter['lastName'] = newValue;
                    // } else {
                    //     newFilter[key] = newValue;
                    // }
                    newFilter[key] = newValue;
                }
            }
        }

        return newFilter;
    }

    buildQueryString(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        return query ? `?${query}` : '';
    }

}